import TagColor from "@core/ui/tag-color/tag-color";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetTransactionModel } from "@factory/model/query-model-transaction";
import { useQyGetTransaction } from "@factory/query/transaction.query";
import ErrorSection from "@shared/ui/error-section/error-section";
import SkeletonList from "@shared/ui/skeleton-list/skeleton-list";
import TextBlock from "@shared/ui/text-block/text-block";
import { currencyFormat } from "@shared/utility/formats/currency-format";
import { DataView } from "primereact/dataview";
import { PaginatorPageChangeEvent } from "primereact/paginator";
import { useState } from "react";

import { appFeature } from "../../../app.feature";
import { useListCardsContext } from "./list-cards.context";

interface CustomerListActivitiesProps {
  action: string;
}

export function CustomerListActivities({
  action,
}: CustomerListActivitiesProps) {
  // THIS IS THE LOCAL DECLARATION
  const { pointsText, stampsText } = appFeature.config.label;
  const { selectedCard } = useListCardsContext();
  const [rowLimit, setRowLimit] = useState(20);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchTerm] = useState("");
  const [first, setFirst] = useState(0);
  const [sort] = useState({});
  const [layout] = useState<
    "grid" | "list" | (string & Record<string, unknown>)
  >("list");

  // THIS IS THE QUERY DECLARATION
  const {
    data: transactionResponse,
    isLoading,
    isError,
    error,
  } = useQyGetTransaction(
    searchTerm,
    rowLimit,
    pageNumber,
    sort,
    { card_series: selectedCard?.code || "", action: action },
    true
  );

  const onPageChange = (event: PaginatorPageChangeEvent) => {
    const offsetValue = event.page * rowLimit;
    setFirst(event.first);
    setPageNumber(offsetValue);
    setRowLimit(event.rows);
  };

  // THIS IS THE LOCAL RENDERS
  const displayLoading = (
    <div className="card">
      <SkeletonList count={4} />
    </div>
  );
  const displayError = (
    <div className="card">
      <ErrorSection title="Error Occured" message={(error as any)?.message} />
    </div>
  );
  const listItem = (transaction: GetTransactionModel, index: number) => {
    return (
      <div
        className="flex justify-between shadow-md rounded-md overflow-hidden border p-5"
        key={transaction.code}
      >
        {action === ActionEnum.Earn ? (
          <div className="flex flex-col">
            <TextBlock label="Reward Name" value={transaction.reward_name} />
            <TextBlock
              label="Amount"
              value={currencyFormat(transaction.amount || 0)}
            />
            <div>
              <TextBlock
                label={`${pointsText} Earned`}
                value={transaction.points?.toString()}
              />
              <TextBlock
                label={`${stampsText} Earned`}
                value={transaction.stamp?.toString()}
              />
              {transaction.coupon && (
                <TextBlock
                  label={`Coupon Used`}
                  value={transaction.coupon_code}
                />
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col">
            <TextBlock
              label="Campaign Redeemed"
              value={transaction.campaign_name}
            />
            <div>
              <TextBlock
                label={`${pointsText} Redeemed`}
                value={`${transaction.points?.toString()}`}
              />
              <TextBlock
                label={`${stampsText} Redeemed`}
                value={`${transaction.stamp?.toString()}`}
              />
              {transaction.coupon && (
                <TextBlock
                  label={`Coupon Used`}
                  value={transaction.coupon_code}
                />
              )}
            </div>
          </div>
        )}
        <div>
          <div className="flex justify-end">
            <TagColor value={transaction?.action} />
          </div>
        </div>
      </div>
    );
  };
  const gridItem = (transaction: GetTransactionModel) => (
    <div
      key={transaction.code}
      className="flex shadow-md rounded-md overflow-hidden border p-5"
    >
      <div className="flex flex-col">
        {action === ActionEnum.Earn ? (
          <div className="flex flex-col">
            <TextBlock label="Reward Name" value={transaction.reward_name} />
            <TextBlock
              label="Amount"
              value={currencyFormat(transaction.amount || 0)}
            />
            <div>
              <TextBlock
                label={`${pointsText} Earned`}
                value={transaction.points?.toString()}
              />
              <TextBlock
                label={`${stampsText} Earned`}
                value={transaction.stamp?.toString()}
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-col">
            <TextBlock
              label="Campaign Redeemed"
              value={transaction.campaign_name}
            />
            <div>
              <TextBlock
                label={`${pointsText} Redeemed`}
                value={`${transaction.points?.toString()}`}
              />
              <TextBlock
                label={`${stampsText} Redeemed`}
                value={`${transaction.stamp?.toString()}`}
              />
            </div>
          </div>
        )}
        <div>
          <div className="flex justify-end">
            <TagColor value={transaction?.action} />
          </div>
        </div>
      </div>
    </div>
  );
  const itemTemplate = (
    transaction: GetTransactionModel,
    layout: "grid" | "list",
    index: number
  ) => {
    if (!transaction) {
      return;
    }
    if (layout === "list") return listItem(transaction, index);
    else if (layout === "grid") return gridItem(transaction);
  };
  const listTemplate = (
    transactions: GetTransactionModel[],
    layout: "grid" | "list"
  ): any => {
    return (
      <div
        className={`grid ${
          layout === "grid" ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"
        } gap-4 mt-4`}
      >
        {transactions.map((transaction, index) =>
          itemTemplate(transaction, layout, index)
        )}
      </div>
    );
  };

  const grid = (
    <section>
      <h5 className="mb-2">
        Total: <b>{transactionResponse?.count}</b>
      </h5>
      <DataView
        value={transactionResponse?.data}
        listTemplate={listTemplate}
        layout={layout}
        paginator
        rows={rowLimit}
        first={first}
        totalRecords={transactionResponse?.count}
        rowsPerPageOptions={[10, 20, 30]}
        onPage={onPageChange}
      />
    </section>
  );

  return (
    <div id="ListActivities" className="campaign">
      <div className="p-4 bg-white">
        {isLoading && displayLoading}
        {isError && !isLoading && displayError}
        {!isLoading && !isError && grid}
      </div>
    </div>
  );
}

export default CustomerListActivities;
